import React, { useEffect, useState } from "react";

import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

export interface DeleteDialogProps {
  open: boolean;
  systemName: string;
  deleteProtection: boolean;
  onClose: () => void;
  onSoftDelete: () => Promise<void>;
  onHardDelete: () => Promise<void>;
  loading: boolean;
}

export function DeleteDialog({
  open,
  systemName,
  deleteProtection,
  onClose,
  onSoftDelete,
  onHardDelete,
  loading,
}: DeleteDialogProps): React.ReactElement {
  const [confirmName, setConfirmName] = useState("");
  const [mode, setMode] = useState<"soft" | "hard">("soft");

  const hardDeleteReady = confirmName === systemName;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setConfirmName("");
      setMode("soft");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningAmberIcon color="warning" />
        Delete Stack
      </DialogTitle>
      <DialogContent>
        {deleteProtection ? (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            <strong>Delete protection is enabled.</strong> Disable it in the
            Configuration tab before deleting.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <DialogContentText>
              Choose how to delete <strong>{systemName}</strong>:
            </DialogContentText>

            <Stack spacing={1.5}>
              <Card
                variant="outlined"
                onClick={() => setMode("soft")}
                sx={{
                  cursor: "pointer",
                  borderColor: mode === "soft" ? "primary.main" : "divider",
                  borderWidth: mode === "soft" ? 2 : 1,
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Archive (soft delete)
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Marks the stack as deleted in the database. Kubernetes
                    namespace, PVCs, and all data are preserved. Can be restored
                    by the platform team.
                  </Typography>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                onClick={() => setMode("hard")}
                sx={{
                  cursor: "pointer",
                  borderColor: mode === "hard" ? "error.main" : "divider",
                  borderWidth: mode === "hard" ? 2 : 1,
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DeleteForeverIcon fontSize="small" color="error" />
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="error"
                    >
                      Permanent delete (hard delete)
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Deletes the Kubernetes namespace and ALL data including
                    PVCs, databases, and object storage.
                    <strong> This cannot be undone.</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Stack>

            {mode === "hard" && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Type <strong>{systemName}</strong> to confirm permanent
                  deletion:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={systemName}
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  error={confirmName.length > 0 && !hardDeleteReady}
                  color="error"
                  inputProps={{ spellCheck: false }}
                />
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {!deleteProtection &&
          (mode === "soft" ? (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => void onSoftDelete()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} /> : undefined}
            >
              Archive
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={() => void onHardDelete()}
              disabled={loading || !hardDeleteReady}
              startIcon={
                loading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DeleteForeverIcon />
                )
              }
            >
              Delete permanently
            </Button>
          ))}
      </DialogActions>
    </Dialog>
  );
}

export default DeleteDialog;
